<template>
  <teleport to="body">
    <div class="overlay" @click.self="fechar">
      <div class="modal-inscritos" @click.stop>
        <!-- Header -->
        <div class="modal-header">
          <span class="modal-label">Inscritos na Atividade</span>
          <button class="btn-fechar-modal" @click="fechar" aria-label="Fechar">
            ×
          </button>
        </div>

        <!-- Body -->
        <div class="modal-inscritos-body" v-if="!carregando">
          <div class="container-tabela" v-if="atividade">
            <h4 class="nome-atividade">{{ atividade.nome }}</h4>

            <div class="atividade-meta">
              <span v-if="nomeSubtipo">
                <i class="ti ti-category"></i> {{ formatarEnum(nomeSubtipo) }}
              </span>
              <span>
                <i class="ti ti-world"></i>
                {{ formatarEnum(atividade.modalidadeExecucao) }}
              </span>
              <span>
                <i class="ti ti-calendar-time"></i>
                {{ formatarDataHora(atividade.dataInicioInscricao) }} -
                {{ formatarDataHora(atividade.dataFimInscricao) }}
              </span>
            </div>

            <!-- Alerta para critério da coordenação -->
            <div v-if="isCriterioCoordenacaoLocal && props.isCoordenador" class="alerta-coordenacao">
              <i class="ti ti-info-circle"></i>
              <span v-if="todasVagasPreenchidas">
                <strong>Critério da Coordenação:</strong> Todas as
                {{ vagasOfertadas }} vaga(s) ofertadas foram preenchidas. Você
                ainda pode excluir participantes desta atividade.
              </span>
              <span v-else>
                <strong>Critério da Coordenação:</strong> Você pode aceitar ou
                excluir participantes desta atividade<template v-if="vagasOfertadas > 0">
                  ({{ totalConfirmados }}/{{ vagasOfertadas }} vaga(s) preenchida(s))</template>.
              </span>
            </div>

            <div class="instrucao">
              <span>Lista de participantes inscritos</span>
              <span class="contador-inscritos">{{ inscritos.length }} inscrito(s)</span>
            </div>

            <!-- Tabela -->
            <div v-if="inscritos.length > 0" class="tabela-customizada">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Data de Solicitação</th>
                    <th>Status</th>
                    <th>Nome Completo</th>
                    <th>E-mail</th>
                    <th v-if="mostrarAcoes">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(inscrito, index) in inscritos" :key="inscrito.id || inscrito.participanteId || index">
                    <td class="col-ordem">{{ index + 1 }}</td>
                    <td>{{ formatarDataInscricao(inscrito) }}</td>
                    <td class="coluna-status">
                      <div class="badge-status" :class="getStatusClass(inscrito.statusInscricao)">
                        {{
                          (
                            formatarEnum(inscrito.statusInscricao) || "-"
                          ).toUpperCase()
                        }}
                      </div>
                    </td>
                    <td>{{ inscrito.nome }}</td>
                    <td class="col-email">{{ inscrito.email }}</td>
                    <td v-if="mostrarAcoes" class="col-acoes">
                      <div class="acoes-btns">
                        <button v-if="podeAceitarInscrito(inscrito)" class="btn-acao btn-aceitar"
                          @click="confirmarAcao('aceitar', inscrito)" :disabled="processando"
                          title="Aceitar participante">
                          <i class="ti ti-check"></i>
                        </button>
                        <button class="btn-acao btn-excluir" @click="confirmarAcao('excluir', inscrito)"
                          :disabled="processando" title="Excluir participante">
                          <i class="ti ti-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="sem-inscritos">
              <i class="ti ti-users-minus"></i>
              <p>Nenhum inscrito encontrado nesta atividade.</p>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div v-else class="modal-inscritos-body loading-state">
          <p>Carregando...</p>
        </div>
      </div>
    </div>

    <!-- Mini-modal de confirmação -->
    <transition name="confirm-fade">
      <div v-if="confirmacao.visivel" class="overlay-confirmacao" @click.self="cancelarConfirmacao">
        <div class="modal-confirmacao" @click.stop>
          <div class="confirm-icone" :class="confirmacao.tipo === 'excluir'
            ? 'confirm-vermelho'
            : 'confirm-verde'
            ">
            <i :class="confirmacao.tipo === 'excluir'
              ? 'ti ti-alert-triangle'
              : 'ti ti-user-check'
              "></i>
          </div>
          <h5 class="confirm-titulo">
            {{
              confirmacao.tipo === "excluir"
                ? "Excluir participante?"
                : "Aceitar participante?"
            }}
          </h5>
          <p class="confirm-msg">
            <template v-if="confirmacao.tipo === 'excluir'">
              Tem certeza que deseja <strong>excluir</strong> o participante
              <strong>{{ confirmacao.inscrito?.nome }}</strong>? Esta ação não pode ser desfeita.
            </template>
            <template v-else>
              Deseja <strong>aceitar</strong> o participante
              <strong>{{ confirmacao.inscrito?.nome }}</strong>
              nesta atividade?
            </template>
          </p>
          <div class="confirm-acoes">
            <button class="btn-confirm-cancelar" @click="cancelarConfirmacao" :disabled="processando">
              Cancelar
            </button>
            <button class="btn-confirm-ok" :class="confirmacao.tipo === 'excluir'
              ? 'btn-confirm-vermelho'
              : 'btn-confirm-verde'
              " @click="executarAcao" :disabled="processando">
              <template v-if="processando"> Processando... </template>
              <template v-else>
                {{ confirmacao.tipo === "excluir" ? "Excluir" : "Aceitar" }}
              </template>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, computed } from "vue";
import {
  formatarData,
  formatarDataHora,
  formatarEnum,
} from "@/utils/FormatarEntradas";
import participantesAtividadeService from "@/service/participantesAtividadeService";
import atividadeService from "@/service/atividadeService";
import { useToast } from "@/composables/useToast";

const toast = useToast();

const props = defineProps({
  atividadeId: {
    type: String,
    required: true,
  },
  isCoordenador: {
    type: Boolean,
    default: false,
  },
  criterioInscricao: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["fechar", "atualizar"]);

const inscritos = ref([]);
const carregando = ref(false);
const processando = ref(false);
const atividade = ref(null);
const resolvedSubtipoNome = ref("");

const confirmacao = ref({
  visivel: false,
  tipo: "",
  inscrito: null,
});

const isCriterioCoordenacaoLocal = computed(() => {
  const criterio =
    props.criterioInscricao || atividade.value?.criterioInscricao || "";
  return (
    criterio === "CRITERIODACOORDENACAO" ||
    criterio === "CRITERIO_DA_COORDENACAO" ||
    criterio === "Critério da Coordenação"
  );
});

const mostrarAcoes = computed(() => {
  return props.isCoordenador && isCriterioCoordenacaoLocal.value;
});

function isInscritoConfirmado(inscrito) {
  const status = String(
    inscrito?.statusInscricao || inscrito?.status || ""
  ).toUpperCase();
  return status === "CONFIRMADA" || status === "CONFIRMADO";
}

const vagasOfertadas = computed(() => {
  const raw =
    atividade.value?.numeroDeVagasOfertadas ??
    atividade.value?.vagasOfertadas ??
    atividade.value?.numeroVagas ??
    0;
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : 0;
});

const totalConfirmados = computed(() => {
  return (inscritos.value || []).filter((inscrito) =>
    isInscritoConfirmado(inscrito)
  ).length;
});

const todasVagasPreenchidas = computed(() => {
  return (
    vagasOfertadas.value > 0 &&
    totalConfirmados.value >= vagasOfertadas.value
  );
});

function podeAceitarInscrito(inscrito) {
  return !todasVagasPreenchidas.value && !isInscritoConfirmado(inscrito);
}

const nomeSubtipo = computed(() => {
  if (!atividade.value) return "";
  if (
    typeof atividade.value.subtipo === "object" &&
    atividade.value.subtipo !== null
  ) {
    return atividade.value.subtipo.nome
      ? atividade.value.subtipo.nome.toLowerCase()
      : "";
  }
  return resolvedSubtipoNome.value;
});

async function carregarDados() {
  carregando.value = true;
  try {
    atividade.value = await atividadeService.getAtividadeId(props.atividadeId);

    resolvedSubtipoNome.value = "";
    if (atividade.value) {
      let subtipoId = null;
      if (
        typeof atividade.value.subtipo === "object" &&
        atividade.value.subtipo !== null
      ) {
        subtipoId = atividade.value.subtipo.id;
      } else {
        subtipoId =
          atividade.value.subtipoId ||
          atividade.value.subtipo_id ||
          atividade.value.subtipo;
      }

      if (subtipoId && !isNaN(subtipoId)) {
        try {
          const response = await atividadeService.getSubTipoPorId(subtipoId);
          if (response && response.data && response.data.nome) {
            resolvedSubtipoNome.value = response.data.nome.toLowerCase();
          }
        } catch (error) {
          console.error("Erro ao carregar o subtipo da atividade:", error);
        }
      }
    }

    const resultado =
      await participantesAtividadeService.getInscritosByAtividade(
        props.atividadeId
      );
    inscritos.value = resultado || [];
  } catch (error) {
    console.error("Erro ao carregar dados:", error);
    toast.erro("Erro ao carregar dados da atividade e inscritos.", {
      titulo: "Erro",
    });
  } finally {
    carregando.value = false;
  }
}

function formatarDataInscricao(inscrito) {
  const data =
    inscrito.dataSolicitacao ||
    inscrito.dataInscricao ||
    inscrito.dataCadastro ||
    inscrito.createdAt ||
    inscrito.dataRegistro ||
    inscrito.dataHoraRegistro ||
    inscrito.dataHoraInscricao;
  if (!data) return "-";
  return formatarData(data) || "-";
}

function getStatusClass(status) {
  if (!status) return "roxo";
  const s = status.toUpperCase();
  if (
    s.includes("CONFIRMADA") ||
    s.includes("APROVADO") ||
    s.includes("ATIVO") ||
    s.includes("CONFIRMADO") ||
    s.includes("ACEITO")
  ) {
    return "verde";
  }
  if (
    s.includes("PENDENTE") ||
    s.includes("SOLICITADO") ||
    s.includes("EM_ANALISE")
  ) {
    return "amarelo";
  }
  if (
    s.includes("CANCELADA") ||
    s.includes("REJEITADA") ||
    s.includes("DESISTENTE") ||
    s.includes("EXCLUIDO")
  ) {
    return "cinza";
  }
  return "roxo";
}

function confirmarAcao(tipo, inscrito) {
  confirmacao.value = {
    visivel: true,
    tipo,
    inscrito,
  };
}

function cancelarConfirmacao() {
  confirmacao.value = { visivel: false, tipo: "", inscrito: null };
}

async function executarAcao() {
  const { tipo, inscrito } = confirmacao.value;
  const participanteId =
    inscrito?.participanteId || inscrito?.id || inscrito?.usuarioId;
  if (tipo === "excluir" && !participanteId) {
    toast.erro("Identificador do participante não encontrado.", {
      titulo: "Erro",
    });
    cancelarConfirmacao();
    return;
  }

  processando.value = true;
  try {
    if (tipo === "excluir") {
      await participantesAtividadeService.removerInscricao(participanteId);
      toast.sucesso(`Participante ${inscrito.nome} excluído com sucesso.`, {
        titulo: "Participante excluído",
      });
    } else if (tipo === "aceitar") {
      // Envia os dados no formato esperado pela rota
      const payload = inscrito.email
        ? { email: inscrito.email }
        : { cpf: inscrito.cpf };
      await atividadeService.escolherParticipantesAtividade(props.atividadeId, [
        payload,
      ]);
      toast.sucesso(`Participante ${inscrito.nome} aceito com sucesso.`, {
        titulo: "Participante aceito",
      });
    }

    cancelarConfirmacao();
    await carregarDados();
    emit("atualizar");
  } catch (error) {
    console.error(`Erro ao ${tipo} participante:`, error);
    toast.erro(
      error?.response?.data?.message ||
      `Erro ao ${tipo} participante. Tente novamente.`,
      { titulo: `Erro ao ${tipo}` }
    );
  } finally {
    processando.value = false;
  }
}

function fechar() {
  emit("fechar");
}

carregarDados();
</script>

<style scoped>
.modal-inscritos {
  border-radius: 16px;
  width: 100%;
  max-width: 960px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow:
    0 20px 25px -5px hsla(0, 0%, 0%, 0.15),
    0 8px 10px -6px hsla(0, 0%, 0%, 0.08);
}


.modal-inscritos-body {
  padding: 1rem;
  background: var(--branco);
  overflow-y: auto;
  flex: 1;
}

.container-tabela {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.nome-atividade {
  font-size: 18px;
  font-weight: 700;
  color: var(--roxo-primario);
  margin: 0 0 8px 0;
  text-transform: uppercase;
  text-align: center;
}

.atividade-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin-bottom: 16px;
  font-size: 14px;
  color: var(--preto);
}

.atividade-meta span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.alerta-coordenacao {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: var(--roxo-superficie);
  border: 1px solid var(--roxo-superficie);
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  color: var(--roxo-primario);
}

.alerta-coordenacao i {
  font-size: 18px;
  flex-shrink: 0;
}

.instrucao {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 12px;
  color: var(--preto);
}

.contador-inscritos {
  font-weight: 600;
  font-size: 12px;
  color: var(--cinza-medio);
  background-color: var(--cinza-claro);
  padding: 4px 12px;
  border-radius: 99px;
}

.tabela-customizada {
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid var(--cinza-claro);
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

thead tr {
  background-color: var(--roxo-primario);
  color: var(--branco);
}

th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-right: 1px solid hsla(0, 0%, 100%, 0.15);
  white-space: nowrap;
}

th:last-child {
  border-right: none;
}

tbody tr {
  background-color: var(--branco);
  border-bottom: 1px solid var(--cinza-claro);
  transition: background-color 0.15s ease;
}

tbody tr:nth-child(even) {
  background-color: var(--branco);
}

tbody tr:hover {
  background-color: var(--roxo-superficie);
}

td {
  padding: 10px 12px;
  text-align: left;
  color: var(--preto);
  vertical-align: middle;
}

.col-ordem {
  text-align: center;
  width: 48px;
  font-weight: 600;
  color: var(--cinza-medio);
}

.col-email {
  font-size: 13px;
  color: var(--cinza-medio);
}

.coluna-status {
  width: 160px;
}

.badge-status {
  padding: 4px 8px;
  border-radius: 4px;
  color: var(--branco);
  font-weight: 600;
  font-size: 12px;
  text-align: center;
  display: inline-block;
  box-shadow: 0 1px 2px hsla(0, 0%, 0%, 0.08);
}

.verde {
  background-color: var(--verde);
}

.amarelo {
  background-color: var(--laranja);
  color: var(--laranja-escuro);
}

.roxo {
  background-color: var(--roxo-medio);
}

.cinza {
  background-color: var(--cinza-medio);
}

.col-acoes {
  width: 120px;
  text-align: center;
}

.acoes-btns {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-acao {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-acao:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-aceitar {
  background-color: var(--verde-claro);
  color: var(--verde);
  border: 1px solid var(--verde-claro);
}

.btn-aceitar:hover:not(:disabled) {
  background-color: var(--verde);
  color: var(--branco);
  border-color: var(--verde);
}

.btn-excluir {
  background-color: var(--vermelho-claro);
  color: var(--vermelho-escuro);
  border: 1px solid var(--vermelho-claro);
}

.btn-excluir:hover:not(:disabled) {
  background-color: var(--vermelho-escuro);
  color: var(--branco);
  border-color: var(--vermelho-escuro);
}

.sem-inscritos {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  color: var(--cinza-medio);
  gap: 8px;
}

.sem-inscritos i {
  font-size: 36px;
  opacity: 0.5;
}

.sem-inscritos p {
  font-size: 14px;
  margin: 0;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.loading-state p {
  font-size: 14px;
  color: var(--cinza-medio);
}

/* ===== Mini-modal de confirmação ===== */
.overlay-confirmacao {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: hsla(0, 0%, 0%, 0.5);
  z-index: 999999;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  box-sizing: border-box;
}

.modal-confirmacao {
  background: var(--branco);
  border-radius: 16px;
  padding: 32px 24px 24px 24px;
  max-width: 400px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 20px 25px -5px hsla(0, 0%, 0%, 0.2);
}

.confirm-icone {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 16px;
}

.confirm-vermelho {
  background-color: var(--vermelho-claro);
  color: var(--vermelho-escuro);
}

.confirm-verde {
  background-color: var(--verde-claro);
  color: var(--verde);
}

.confirm-titulo {
  font-size: 18px;
  font-weight: 700;
  color: var(--preto);
  margin: 0 0 8px 0;
}

.confirm-msg {
  font-size: 14px;
  color: var(--cinza-medio);
  line-height: 1.5;
  margin: 0 0 24px 0;
}

.confirm-msg strong {
  color: var(--preto);
}

.confirm-acoes {
  display: flex;
  gap: 12px;
  width: 100%;
}

.btn-confirm-cancelar {
  flex: 1;
  padding: 10px 16px;
  background: transparent;
  color: var(--cinza-escuro);
  border: 1px solid var(--cinza-claro);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-confirm-cancelar:hover {
  background-color: var(--cinza-claro);
  border-color: var(--cinza-medio);
}

.btn-confirm-ok {
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--branco);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-confirm-ok:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-confirm-vermelho {
  background-color: var(--vermelho-escuro);
}

.btn-confirm-vermelho:hover:not(:disabled) {
  background-color: var(--vermelho-escuro);
}

.btn-confirm-verde {
  background-color: var(--verde);
}

.btn-confirm-verde:hover:not(:disabled) {
  background-color: var(--verde-escuro);
}

/* Transições */
.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.2s ease;
}

.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}

/* Responsividade */
@media (max-width: 768px) {
  .modal-inscritos-body {
    padding: 16px;
  }

  .modal-inscritos {
    max-width: 98%;
  }

  .atividade-meta {
    flex-direction: column;
    gap: 4px;
    align-items: center;
  }

  th,
  td {
    padding: 8px;
  }

  .coluna-status {
    width: 120px;
  }

  .col-acoes {
    width: 90px;
  }
}
</style>
